import { visibilityBand } from './visibilityBands';
export interface ShootingQualityInput {
  /** The existing MW visibility rating (0-10). Required. */
  mwVisibilityRating: number;
  /** Galactic core max elevation in degrees during visibility window. */
  maxElevation?: number;
  /** Duration of astronomical darkness in minutes. */
  darknessWindowMinutes?: number;
  /** Bortle class at the location (1-9). Lower = darker sky. */
  bortleClass?: number;
}

export interface QualityLabel {
  label: string;
  color: string;
}

/**
 * Compute a supplementary shooting quality score (0-10) that enriches the
 * MW visibility rating with observing-condition factors.
 *
 * Design constraints:
 * - Returns 0 whenever the MW visibility rating is 0 (core not visible).
 * - Uses the MW rating as the base — the score can be adjusted up or down
 *   by at most 2 points to keep it consistent with the primary rating.
 * - Missing optional inputs are treated as neutral (no bonus, no penalty).
 */
export function getShootingQualityScore(input: ShootingQualityInput): number {
  const { mwVisibilityRating, maxElevation, darknessWindowMinutes, bortleClass } = input;

  if (mwVisibilityRating === 0) return 0;

  let adjustment = 0;

  // Core altitude bonus: higher altitude means better framing and less atmospheric distortion
  if (maxElevation !== undefined) {
    if (maxElevation >= 45) adjustment += 1.0;
    else if (maxElevation >= 30) adjustment += 0.5;
    else if (maxElevation < 15) adjustment -= 0.5;
  }

  // Darkness window bonus: longer window = more shooting time
  if (darknessWindowMinutes !== undefined) {
    if (darknessWindowMinutes >= 240) adjustment += 0.5; // 4+ hours
    else if (darknessWindowMinutes >= 180) adjustment += 0.25; // 3+ hours
    else if (darknessWindowMinutes < 60) adjustment -= 0.5; // < 1 hour
  }

  // Bortle penalty: higher Bortle = more light pollution
  if (bortleClass !== undefined) {
    if (bortleClass <= 2) adjustment += 0.5; // Truly dark site bonus
    else if (bortleClass >= 7) adjustment -= 1.5; // Suburban/urban penalty
    else if (bortleClass >= 6) adjustment -= 1.0;
    else if (bortleClass >= 5) adjustment -= 0.5;
    // Bortle 3-4 = neutral (typical dark sky site)
  }

  // Clamp adjustment to [-2, +2]
  adjustment = Math.max(-2, Math.min(2, adjustment));

  const score = Math.round(mwVisibilityRating + adjustment);
  return Math.max(0, Math.min(10, score));
}

/**
 * Parse astronomical twilight time strings and return darkness window in minutes.
 * Returns 0 if times are not valid time strings.
 */
export function getDarknessWindowMinutes(
  astroTwilightEvening: string,
  astroTwilightMorning: string
): number {
  const eveningMinutes = parseTimeToMinutes(astroTwilightEvening);
  const morningMinutes = parseTimeToMinutes(astroTwilightMorning);

  if (eveningMinutes === null || morningMinutes === null) return 0;

  // Morning is next day if earlier than evening
  let duration = morningMinutes - eveningMinutes;
  if (duration < 0) duration += 24 * 60;

  return duration;
}

function parseTimeToMinutes(timeStr: string): number | null {
  // Match "HH:MM AM/PM" format
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  return hours * 60 + minutes;
}

/**
 * Get a human-readable quality label and color for a shooting quality score.
 */
export function getQualityLabel(score: number): QualityLabel {
  // This is a different metric from the visibility rating (it folds in elevation,
  // darkness minutes and Bortle), but it uses the same words on the same 0-10
  // scale, so it has to use the same boundaries. It previously had no "Very Good"
  // at all and started "Good" at 6, which made the same word mean two things
  // depending on which card you were reading. The floor label stays "Not Viable"
  // rather than "Not Visible" on purpose: a night can be perfectly visible and
  // still not worth shooting once Bortle and darkness are counted.
  const colors: Record<string, string> = {
    'Excellent': 'text-green-500',
    'Very Good': 'text-green-400',
    'Good': 'text-gold',
    'Fair': 'text-yellow-500',
    'Poor': 'text-orange-500',
  };
  const band = visibilityBand(score);
  if (band === 'Not Visible') return { label: 'Not Viable', color: 'text-red-500' };
  return { label: band, color: colors[band] };
}
